import { PurchaseLabelError } from "@redotech/merchant-sdk/rpc/schema/purchase-label";
import {
  t as defaultT,
  type Translate,
  useTranslation,
} from "@redotech/redo-i18n";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { CurrencyContext } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import { useContext } from "react";
import { ConfirmationPageInfo } from "../create-label-modal-types";

function getErrorMessage(
  error: PurchaseLabelError | undefined,
  t: Translate = defaultT,
): string {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (error) {
    case PurchaseLabelError.LABEL_PRICE_TOO_HIGH:
      return t(
        "The selected shipping rate exceeds the maximum label price limit.",
      );
    case PurchaseLabelError.UPS_PHONE_NUMBER_TOO_SHORT:
      return t(
        "UPS requires phone numbers to be at least 10 digits. Please update the phone number on the address and try again.",
      );
    case PurchaseLabelError.UPS_MISSING_SHIP_FROM_NAME:
      return t(
        "UPS requires a name on the ship from address. Please update the customer's name and try again.",
      );
    case PurchaseLabelError.USPS_PHONE_NUMBER_INVALID:
      return t(
        "USPS requires phone numbers to be exactly 10 digits. Please update the phone number on the address and try again.",
      );
    case PurchaseLabelError.MISSING_CUSTOMER_NAME:
      return t(
        "A name is required on the customer address. Please update the customer's name and try again.",
      );
    case PurchaseLabelError.CARRIER_ERROR:
      return t(
        "Please double check that the address and weight are correct or try again with a different label.",
      );
    default:
      return t("We encountered an unexpected error.");
  }
}

export const LabelConfirmation = ({
  confirmationPageInfo,
}: {
  confirmationPageInfo?: ConfirmationPageInfo;
}) => {
  const { t } = useTranslation();
  const { formatCurrency } = useContext(CurrencyContext);

  if (confirmationPageInfo?.success) {
    return (
      <Flex dir="column" gap="md">
        <RedoAlert
          color="success"
          description={
            confirmationPageInfo.charge
              ? t("{{amount}} was added to your next invoice.", {
                  amount: formatCurrency(confirmationPageInfo.charge),
                })
              : undefined
          }
          Icon={CheckCircleIcon}
          title={
            confirmationPageInfo.kind === "dropoff"
              ? t("Your drop-off has been created.")
              : t("Your label has been created.")
          }
        />
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="md">
      <RedoAlert
        color="error"
        description={t(
          "If the problem persists, please reach out to support for help.",
        )}
        title={getErrorMessage(confirmationPageInfo?.error, t)}
      />
    </Flex>
  );
};
